import { useMobileAuthStore } from '@/stores/mobileAuth'
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { MobileAuthApiClient } from '../generated/mobileAuthApi'
import { Token } from '../libs/deviceApi/Token'

const isExpired = (tokenRaw: string): boolean => {
  return Token.parseOrNull(tokenRaw)?.isExpired() ?? true
}

const applyAuthHeader = (config: InternalAxiosRequestConfig<unknown>, token: string) => {
  config.headers['Authorization'] = `Bearer ${token}`
}

async function authHeaderRequestInterceptor(
  config: InternalAxiosRequestConfig<unknown>,
  apiClientNoAuth: MobileAuthApiClient
) {
  const mobileAuthStore = useMobileAuthStore()

  const accessToken = mobileAuthStore.accessToken.value

  if (accessToken === null) {
    return config
  }

  if (!isExpired(accessToken)) {
    applyAuthHeader(config, accessToken)
    return config
  }

  const refreshToken = mobileAuthStore.refreshToken.value
  if (refreshToken === null) {
    return config
  }

  const newTokens = await apiClientNoAuth.auth.postAuthRefresh({
    refreshToken: refreshToken
  })

  await mobileAuthStore.onMobileAuthTokenRefresh(newTokens)

  applyAuthHeader(config, newTokens.accessToken)
  return config
}

export function axiosForMobileAuth(mobileAuthApiBaseUrl: string): AxiosInstance {
  const axiosInstance = axios.create()
  const apiClientNoAuth = new MobileAuthApiClient({ BASE: mobileAuthApiBaseUrl })

  axiosInstance.interceptors.request.use((config) =>
    authHeaderRequestInterceptor(config, apiClientNoAuth)
  )

  return axiosInstance
}
